import { MapStateToPropsFactory, MapDispatchToProps } from "react-redux";
import { Dispatch } from "redux";

import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import {
  UI__BETSLIP_EXC_RECEIPT_PANEL_DONE_CLICK,
  UI__BETSLIP_EXC_UNMATCHED_CANCEL_CLICK,
  BetslipExchangeMatchedPanelDoneClickAction,
  BetslipExchangeUnmatchedCancelClickAction,
  BetslipExchangeReportBetEditClickAction,
  UI__BETSLIP_EXC_REPORT_EDIT_BET_CLICK,
} from "@ppb/tbd-store/actions/betslip";
import type { URN } from "../../types";
import { getExchangeOrder } from "../../store/state/exchange-orders/exchange-orders-selectors";
import { type BetslipExchangeOrder } from "../../store/state/exchange-orders/ExchangeOrders.types";
import { getExchangeRunnerTree } from "../../store/state/exchange-markets/exchange-markets-selectors";
import { getExchangePotentialState } from "../../store/state/exchange-betting-potential/exchange-betting-potential-selectors";
import { emitMatchMePreferenceToggled } from "../../contract/betslip-api";
import {
  getBetslipExchangeContext,
  getBetslipExchangeReport,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";

import type { ExchangeInlineReceiptPanelProps } from "./snowflakes/ExchangeInlineReceiptPanel/ExchangeInlineReceiptPanel.types";
import { buildExchangeInlineReceipt } from "./exchange-inline-receipt-mapper";
import { i18n } from "../../helpers/i18n";
import { shouldDisplayBetWithMatchMe } from "../../helpers/best-price-execution";
import type {
  BetslipLocalization,
  BetslipSession,
  ExchangeBetslipCapabilities,
  ExchangeBetslipPreferences,
  ExchangeCurrencyDetails,
} from "../../contract";
import {
  BetslipBetWithMatchMeClickAction,
  UI__BETSLIP_BET_WITH_MATCH_ME_CLICK,
} from "../../store/state/betslip-module/betslip-module-exchange-actions";
import type { PlacedBetValues, BetslipExchangeContext } from "../../store/state/betslip-module/betslip-module.types";

export type CardProps = {
  receipt?: ExchangeInlineReceiptPanelProps;
  order: BetslipExchangeOrder | null;
  runner?: URN;
  shouldDisplayBetWithMatchMe?: boolean;
  oddsRange: { min: number; max: number } | null;
  isMatchMeAvailable?: boolean;
  exchangeContext?: BetslipExchangeContext;
  betValues?: PlacedBetValues;
};

type EmptyStateProps = Record<string, never>;
type OrderOnlyStateProps = {
  order: null;
};

export type StateProps = CardProps | EmptyStateProps | OrderOnlyStateProps;

export type DispatchProps = {
  dispatchPanelDone: () => void;
  dispatchPanelEdit: (betId: string, betOriginURL: string) => void;
  dispatchPanelCancel: (betId: string, runner: URN) => void;
  dispatchPanelPlace: (exchangeContext: BetslipExchangeContext, betValues?: PlacedBetValues) => void;
  dispatchToggleMatchMe: () => void;
};
export type ContainerProps = {
  localization: BetslipLocalization;
  currencyDetails: ExchangeCurrencyDetails;
  session: BetslipSession;
  capabilities: ExchangeBetslipCapabilities;
  preferences: ExchangeBetslipPreferences;
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  return (
    appState: BetslipApplicationState,
    { localization, currencyDetails, session, capabilities, preferences }: ContainerProps,
  ): StateProps => {
    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    const exchangeContext = getBetslipExchangeContext(appState);
    const exchangeReport = getBetslipExchangeReport(appState);

    if (!exchangeReport) {
      return {
        order: null,
      };
    }

    const { runner: runnerUrn, betIds, side } = exchangeReport;
    const remnantUnmatchedSize = exchangeReport.unmatched?.size;
    const originalPrice =
      exchangeReport.priceAtSelection ?? exchangeReport.unmatched?.price ?? exchangeReport.matched?.price;
    const betValues =
      originalPrice !== undefined && remnantUnmatchedSize !== undefined
        ? { price: originalPrice, size: remnantUnmatchedSize }
        : exchangeReport.unmatched ?? exchangeReport.matched;

    const runnerTree = getExchangeRunnerTree(appState, runnerUrn);
    const order = runnerTree && betIds ? getExchangeOrder(appState, runnerTree.market.urn, betIds[0]) : null;
    const receipt = buildExchangeInlineReceipt(appState, {
      localization,
      currencyDetails,
      session,
      capabilities,
      preferences,
    });

    const potentialState = getExchangePotentialState(appState, runnerUrn, side);
    const priceAtPlacement = potentialState?.priceAtPlacement;
    const hasPotentialRange = !!(
      potentialState?.price &&
      priceAtPlacement &&
      Math.abs(potentialState?.price - priceAtPlacement) !== 0
    );
    const oddsRange =
      hasPotentialRange && potentialState.price ? { min: potentialState.price, max: priceAtPlacement } : null;

    const matchMeSecondaryLabel = oddsRange
      ? i18n({ key: "I18N.MATCH_ME.ODDS_RANGE", interpolationValues: { min: oddsRange.min, max: oddsRange.max } })
      : undefined;

    const enrichedReceipt = receipt?.unmatched
      ? { ...receipt, unmatched: { ...receipt.unmatched, matchMeSecondaryLabel } }
      : receipt;

    return {
      runner: runnerUrn,
      order,
      receipt: enrichedReceipt,
      oddsRange,
      shouldDisplayBetWithMatchMe: shouldDisplayBetWithMatchMe(
        appState,
        currencyDetails,
        session,
        capabilities,
        preferences,
      ),
      ...(preferences.matchMe.available ? { isMatchMeAvailable: true } : {}),
      ...(exchangeContext ? { exchangeContext } : {}),
      ...(betValues ? { betValues } : {}),
    };
  };
};

export const makeMapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchPanelDone: () => {
    dispatch<BetslipExchangeMatchedPanelDoneClickAction>({
      type: UI__BETSLIP_EXC_RECEIPT_PANEL_DONE_CLICK,
    });
  },
  dispatchPanelEdit: (betId: string, betOriginURL: string) => {
    dispatch<BetslipExchangeReportBetEditClickAction>({
      type: UI__BETSLIP_EXC_REPORT_EDIT_BET_CLICK,
      payload: {
        betId,
        isPersistenceTypeMenuExpanded: false,
        betOriginURL,
      },
    });
  },
  dispatchPanelCancel: (betId: string, runner: URN) => {
    dispatch<BetslipExchangeUnmatchedCancelClickAction>({
      type: UI__BETSLIP_EXC_UNMATCHED_CANCEL_CLICK,
      payload: {
        instructions: { betIds: [betId], runner },
      },
    });
  },
  dispatchPanelPlace: (exchangeContext: BetslipExchangeContext, betValues?: PlacedBetValues) => {
    dispatch<BetslipBetWithMatchMeClickAction>({
      type: UI__BETSLIP_BET_WITH_MATCH_ME_CLICK,
      payload: {
        marketURN: exchangeContext.market,
        runner: exchangeContext.runner,
        side: exchangeContext.side,
        marketDepth: exchangeContext.marketDepth,
        price: betValues?.price,
        size: betValues?.size,
      },
    });
  },
  dispatchToggleMatchMe: () => {
    emitMatchMePreferenceToggled({ enabled: true, surface: "RECEIPT" });
  },
});
